import React from 'react';

import { RatchetModel, RatchetModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';

import { ColumnState, GridApi } from 'ag-grid-community';
import { conversionModelsThresholdsMap } from 'src/components/AddPrimeAMFRateCard/AddContractTerms/AddConversionModel/conversionModelThresholds';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { wmgBlendedRateGridColIdSuffix, wmgBlendedRateGridColNameSuffix } from 'src/constants/rateCards';
import { AppContext } from 'src/contexts/appContext';
import { IRateCardGridRowData } from 'src/types/IRateCard';
import { getThresholdDisplayName, getThresholdFieldName } from 'src/util/thresholdUtil';

import { getCppMauColDefs, showPlayCustomerProgramTypeCols } from '../cppMauColDefs';
import './ViewCppGrid.scss';

interface IViewCppGridProps {
    cppRowData: IRateCardGridRowData[];
    conversion?: RatchetModel;
    tertiaryConversion?: RatchetModel;
    filterText: string;
    setCppGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
    dataLoaded?: boolean;
}

export const ViewCppGrid = ({
    cppRowData,
    conversion,
    tertiaryConversion,
    filterText,
    setCppGridApi,
    dataLoaded = true
}: IViewCppGridProps) => {
    const { metadata } = React.useContext(AppContext);
    const colDefs = getCppMauColDefs(metadata.territoryMapping);

    const sortModel: ColumnState[] = [
        { colId: 'playType', sort: 'asc', sortIndex: 0 },
        { colId: 'programType', sort: 'asc', sortIndex: 1 },
        { colId: 'territory', sort: 'asc', sortIndex: 2 }
    ];

    // Dynamically add ratchet cols
    if (
        conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_UMG ||
        conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG
    ) {
        // remove default per play rate column def
        colDefs.pop();
        const umgRatchets = conversionModelsThresholdsMap[conversion?.ratchetModel];
        umgRatchets.forEach((ratchet, index) => {
            colDefs.push({
                field: getThresholdFieldName(index + 1),
                headerName: getThresholdDisplayName(umgRatchets[index])
            });
        });
    } else {
        conversion?.ratchets?.forEach((ratchet) => {
            colDefs.push({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                field: getThresholdFieldName(ratchet.threshold!),
                headerName: getThresholdDisplayName(ratchet)
            });
        });
    }

    tertiaryConversion?.ratchets?.forEach((ratchet) => {
        colDefs.push({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            field: getThresholdFieldName(ratchet.threshold!, wmgBlendedRateGridColIdSuffix),
            headerName: getThresholdDisplayName(ratchet, wmgBlendedRateGridColNameSuffix)
        });
    });

    showPlayCustomerProgramTypeCols(cppRowData, colDefs);

    return (
        <Column spacing="300" heights={['fit', 'fill']}>
            <Text type="h400">Cost per play</Text>
            <Column minHeight={'400px'}>
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={cppRowData}
                    sortModel={sortModel}
                    suppressRowClickSelection={true}
                    suppressSizeColumnsToFit={true}
                    filterText={filterText}
                    setAgGridApi={setCppGridApi}
                    enableRangeSelection={true}
                    loading={!dataLoaded}
                />
            </Column>
        </Column>
    );
};
